import api from '@/services/api'
import type { paths } from './schema'
import type { Endpoint } from './endpoints'
import { toApiError } from './errors'

/** The generated operation type behind an ENDPOINTS entry. */
export type Operation<E extends Endpoint> = paths[E['path']][E['method']]

type Params<O> = O extends { parameters: infer P } ? P : never
export type PathParams<O> = Params<O> extends { path: infer X } ? X : never
export type QueryParams<O> = Params<O> extends { query?: infer Q } ? Q : never
type HeaderParams<O> = Params<O> extends { header?: infer H } ? NonNullable<H> : never
type RequestBody<O> = O extends { requestBody?: infer R } ? NonNullable<R> : never
// `[X] extends [never]` guards: generated operations spell "no body/header" as `?: never`.
export type Body<O> = [RequestBody<O>] extends [never] ? never
  : RequestBody<O> extends { content: { 'application/json': infer B } } ? B : never
type NeedsIdempotencyKey<O> = [HeaderParams<O>] extends [never] ? false
  : 'Idempotency-Key' extends keyof HeaderParams<O> ? true : false
type BodyRequired<O> = O extends { requestBody: object } ? true : false

type Success<R> = 200 extends keyof R ? R[200] : 201 extends keyof R ? R[201] : 202 extends keyof R ? R[202] : 204 extends keyof R ? R[204] : never
type Payload<X> = X extends { content: { 'application/json': infer J } } ? J : X extends { content: object } ? Blob : void
/** What an operation resolves to: its 2xx JSON body, a Blob for binary content, void for 204. */
export type Result<O> = O extends { responses: infer R } ? Payload<Success<R>> : never

type Empty = Record<never, never>
type If<C, T> = [C] extends [never] ? Empty : [C] extends [undefined] ? Empty : T

/** Adapter arguments derived from the schema: path params, query, JSON body, and a required
 *  `idempotencyKey` whenever the operation declares an `Idempotency-Key` header. */
export type Args<O> = { signal?: AbortSignal }
  & If<PathParams<O>, { path: PathParams<O> }>
  & If<QueryParams<O>, Empty extends QueryParams<O> ? { query?: QueryParams<O> } : { query: QueryParams<O> }>
  & If<Body<O>, BodyRequired<O> extends true ? { body: Body<O> } : { body?: Body<O> }>
  & (NeedsIdempotencyKey<O> extends true ? { idempotencyKey: string } : Empty)

export type Adapter<O> = Empty extends Args<O>
  ? (args?: Args<O>) => Promise<Result<O>>
  : (args: Args<O>) => Promise<Result<O>>

export interface SendArgs {
  path?: Record<string, string | number>
  query?: object
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal
}

interface SendOptions {
  responseType?: 'blob'
  headers?: Record<string, string>
}

const API_PREFIX = /^\/api\/v1/

/** Build the request for one ENDPOINTS entry and send it through `api.request`. Rejects with ApiError. */
export async function send<T>(endpoint: Endpoint, args: SendArgs = {}, options: SendOptions = {}): Promise<T> {
  const url = endpoint.path.replace(API_PREFIX, '').replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = args.path?.[name]
    if (value === undefined || value === '') throw new Error(`${endpoint.id}: missing path parameter ${name}`)
    return encodeURIComponent(String(value))
  })
  const headers: Record<string, string> = { ...options.headers }
  if (args.idempotencyKey !== undefined) headers['Idempotency-Key'] = args.idempotencyKey
  try {
    return await api.request<T>({
      method: endpoint.method,
      url,
      params: args.query,
      data: args.body,
      headers,
      signal: args.signal,
      responseType: options.responseType,
    })
  } catch (error) {
    throw toApiError(error)
  }
}

/** One typed adapter function for one ENDPOINTS entry. */
export function bind<E extends Endpoint>(endpoint: E, options?: SendOptions): Adapter<Operation<E>> {
  return ((args?: SendArgs) => send(endpoint, args, options)) as Adapter<Operation<E>>
}
